import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CalendarDays, Users } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PersonTypeBadge } from "@/components/people/PersonTypeBadge";
import type { PersonType } from "@/hooks/usePeople";
import {
  AvailabilityStatusBadge,
  availabilityStatuses,
} from "@/components/project-detail/people/AvailabilityStatusBadge";
import { useCrewPlanningAvailability } from "@/hooks/useCrewPlanning";
import {
  AvailabilityStatus,
  useUpsertAvailability,
} from "@/hooks/useProjectPeople";
import { useProjectWriteAvailability } from "@/hooks/useProjects";
import { groupCrewPlanningAvailability } from "@/lib/crew-planning";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const defaultStatuses: AvailabilityStatus[] = ["unknown", "requested"];
const personTypeFilters: PersonType[] = ["employee", "freelancer", "contact"];

function todayUtcMs() {
  const today = new Date();
  return Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
}

export default function CrewPlanningPage() {
  const {
    data: availability,
    isLoading,
    isError,
    error,
  } = useCrewPlanningAvailability();
  const upsertAvailability = useUpsertAvailability();
  const writeAvailability = useProjectWriteAvailability();
  const [selectedStatuses, setSelectedStatuses] =
    useState<AvailabilityStatus[]>(defaultStatuses);
  const [selectedPersonTypes, setSelectedPersonTypes] =
    useState<PersonType[]>(personTypeFilters);
  const [updatingIds, setUpdatingIds] = useState<string[]>([]);
  const [failedUpdates, setFailedUpdates] = useState<Record<string, string>>(
    {},
  );

  const groups = useMemo(
    () =>
      groupCrewPlanningAvailability({
        availability,
        selectedStatuses,
        selectedPersonTypes,
        todayUtcMs: todayUtcMs(),
      }),
    [availability, selectedPersonTypes, selectedStatuses],
  );

  const visibleRowCount = groups.reduce(
    (count, group) => count + group.rows.length,
    0,
  );

  const toggleStatus = (status: AvailabilityStatus) => {
    setSelectedStatuses((current) =>
      current.includes(status)
        ? current.filter((value) => value !== status)
        : [...current, status],
    );
  };

  const togglePersonType = (personType: PersonType) => {
    setSelectedPersonTypes((current) =>
      current.includes(personType)
        ? current.filter((value) => value !== personType)
        : [...current, personType],
    );
  };

  const updateStatus = async (
    availabilityId: string,
    projectId: string,
    personId: string,
    status: AvailabilityStatus,
    days: string[],
    notes: string | null | undefined,
  ) => {
    if (!writeAvailability.canWrite) {
      toast.error(writeAvailability.reason ?? "Availability is read-only");
      return;
    }

    setFailedUpdates((current) => {
      const next = { ...current };
      delete next[availabilityId];
      return next;
    });
    setUpdatingIds((current) => [...current, availabilityId]);

    try {
      await upsertAvailability.mutateAsync({
        project_id: projectId,
        person_id: personId,
        status,
        available_days: days,
        notes: notes ?? null,
      });
    } catch (updateError) {
      const message =
        updateError instanceof Error
          ? updateError.message
          : "Availability update failed";
      setFailedUpdates((current) => ({
        ...current,
        [availabilityId]: message,
      }));
      toast.error(`Failed to update availability: ${message}`);
    } finally {
      setUpdatingIds((current) =>
        current.filter((id) => id !== availabilityId),
      );
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Crew Planning" />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Crew Planning
          </h1>
          <p className="text-sm text-muted-foreground">
            Future projects with pending person availability.
          </p>
        </div>
        <div className="flex flex-col gap-3 rounded-md border px-3 py-2">
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase text-muted-foreground">
              Status
            </p>
            <div className="flex flex-wrap items-center gap-4">
              {defaultStatuses.map((status) => (
                <div key={status} className="flex items-center gap-2">
                  <Checkbox
                    id={`crew-planning-filter-${status}`}
                    checked={selectedStatuses.includes(status)}
                    onCheckedChange={() => toggleStatus(status)}
                  />
                  <Label
                    htmlFor={`crew-planning-filter-${status}`}
                    className="cursor-pointer"
                  >
                    <AvailabilityStatusBadge status={status} />
                  </Label>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t pt-3">
            <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">
              Person type
            </p>
            <div className="flex flex-wrap items-center gap-4">
              {personTypeFilters.map((personType) => (
                <div key={personType} className="flex items-center gap-2">
                  <Checkbox
                    id={`crew-planning-person-type-${personType}`}
                    checked={selectedPersonTypes.includes(personType)}
                    onCheckedChange={() => togglePersonType(personType)}
                  />
                  <Label
                    htmlFor={`crew-planning-person-type-${personType}`}
                    className="cursor-pointer"
                  >
                    <PersonTypeBadge type={personType} />
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!writeAvailability.canWrite && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Updates blocked</AlertTitle>
          <AlertDescription>
            {writeAvailability.reason ?? "Availability changes are read-only."}
          </AlertDescription>
        </Alert>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Could not load crew planning</AlertTitle>
          <AlertDescription>
            {error?.message ??
              "The crew planning overview could not be loaded."}
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[0, 1, 2].map((item) => (
            <Card key={item}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : visibleRowCount === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground">
          <Users className="mx-auto mb-4 h-12 w-12 opacity-50" />
          <p className="font-medium text-foreground">No pending crew rows</p>
          <p className="text-sm">
            No people match the selected filters on future projects.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <Card key={group.person.id}>
              <CardHeader className="pb-3">
                <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
                  <Link
                    to={`/people/${group.person.id}`}
                    className="text-foreground hover:underline"
                  >
                    {group.person.first_name} {group.person.last_name}
                  </Link>
                  {group.person.person_type && (
                    <PersonTypeBadge
                      type={group.person.person_type as PersonType}
                    />
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {group.rows.map((row) => {
                    const isUpdating = updatingIds.includes(
                      row.availability.id,
                    );
                    const failedUpdate = failedUpdates[row.availability.id];

                    return (
                      <div
                        key={row.availability.id}
                        className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_190px] sm:items-center"
                      >
                        <div className="min-w-0">
                          <Link
                            to={`/projects/${row.project.id}/people/availability`}
                            className="font-medium text-foreground hover:underline"
                          >
                            {row.project.name}
                          </Link>
                          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                            <CalendarDays className="h-4 w-4" />
                            <span>{row.dateLabel}</span>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Select
                            value={row.availability.status}
                            disabled={isUpdating || !writeAvailability.canWrite}
                            onValueChange={(value) =>
                              updateStatus(
                                row.availability.id,
                                row.project.id,
                                row.availability.person_id,
                                value as AvailabilityStatus,
                                row.availability.available_days ?? [],
                                row.availability.notes,
                              )
                            }
                          >
                            <SelectTrigger
                              className={cn(
                                "w-full",
                                failedUpdate && "border-destructive",
                              )}
                              aria-label={`Availability status for ${row.project.name}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availabilityStatuses.map((status) => (
                                <SelectItem key={status} value={status}>
                                  <AvailabilityStatusBadge
                                    status={status}
                                    showIcon={false}
                                  />
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {failedUpdate && (
                            <div className="flex items-center justify-between gap-2 text-xs text-destructive">
                              <span className="min-w-0 truncate">
                                {failedUpdate}
                              </span>
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs text-destructive"
                                onClick={() =>
                                  updateStatus(
                                    row.availability.id,
                                    row.project.id,
                                    row.availability.person_id,
                                    row.availability.status,
                                    row.availability.available_days ?? [],
                                    row.availability.notes,
                                  )
                                }
                              >
                                Retry
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
